import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { BaseUrl } from "../../BaseUrl";
// import { useDoctorAuthStore } from "../../store/useDoctorAuthStore"; // Commented out for Alpha porting

export default function AccountSettings({
  doctor,
  emailVerificationRequired,
  setEmailVerificationRequired,
}) {
  const navigate = useNavigate();
  // const { setDoctor } = useDoctorAuthStore(); // Commented out
  const setDoctor = () => { }; // Mock function

  const email =
    doctor?.personalDetails?.emailId || doctor?.emailId || "example@gmail.com";
  const secondaryEmail = doctor?.secondaryEmailId || null;
  const country = doctor?.addressPerKyc?.country || doctor?.country || "India";
  const googleEmail = doctor?.googleEmail || email;
  const isGoogleConnected =
    doctor?.googleEmail && doctor?.googleEmail !== email;

  // Modal states
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showCountryInfoModal, setShowCountryInfoModal] = useState(false);
  const [showDisconnectGoogleModal, setShowDisconnectGoogleModal] =
    useState(false);
  const [showLogoutAllModal, setShowLogoutAllModal] = useState(false);

  // Form states
  const [newEmail, setNewEmail] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Load email verification setting from doctor data
  useEffect(() => {
    if (doctor?.emailVerificationRequired !== undefined) {
      setEmailVerificationRequired(doctor.emailVerificationRequired);
    }
  }, [doctor, setEmailVerificationRequired]);

  const handleAddEmail = async () => {
    if (!newEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const doctorId = doctor?._id;
      if (!doctorId) {
        toast.error("Doctor ID not found");
        return;
      }

      const res = await axios.put(
        `${BaseUrl}/doctor/doctorProfile/update/${doctorId}`,
        {
          secondaryEmailId: newEmail.trim(),
        },
        { withCredentials: true }
      );

      if (res.data.success || res.status === 200) {
        toast.success("Additional email address added successfully");
        setNewEmail("");
        setShowAddEmailModal(false);
        // Update doctor in store
        if (res.data.data) {
          setDoctor((prev) => ({
            ...prev,
            ...res.data.data,
            secondaryEmailId: res.data.data.secondaryEmailId || newEmail.trim(),
          }));
        } else {
          // Fallback: update secondaryEmailId directly if data structure is different
          setDoctor((prev) => ({
            ...prev,
            secondaryEmailId: newEmail.trim(),
          }));
        }
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to add email address"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    // Strong validation
    if (newPassword.length < 12) {
      toast.error("Password must be at least 12 characters");
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      toast.error("Password must contain at least one uppercase letter");
      return;
    }

    if (!/[a-z]/.test(newPassword)) {
      toast.error("Password must contain at least one lowercase letter");
      return;
    }

    if (!/\d/.test(newPassword)) {
      toast.error("Password must contain at least one number");
      return;
    }

    if (!/[@$!%*?&]/.test(newPassword)) {
      toast.error("Password must contain at least one special character (@$!%*?&)");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    setLoading(true);
    try {
      const doctorId = doctor?._id;
      if (!doctorId) {
        toast.error("Doctor ID not found");
        return;
      }

      const res = await axios.put(
        `${BaseUrl}/doctor/change-password`,
        {
          oldPassword,
          newPassword,
        },
        { withCredentials: true }
      );

      if (res.data.success || res.status === 200) {
        toast.success("Password changed successfully");
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setShowChangePasswordModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerificationToggle = async (checked) => {
    setEmailVerificationRequired(checked);

    try {
      const doctorId = doctor?._id;
      if (!doctorId) {
        toast.error("Doctor ID not found");
        setEmailVerificationRequired(!checked); // Revert on error
        return;
      }

      const res = await axios.put(
        `${BaseUrl}/doctor/doctorProfile/update/${doctorId}`,
        {
          emailVerificationRequired: checked,
        },
        { withCredentials: true }
      );

      if (res.data.success || res.status === 200) {
        toast.success("Email verification setting updated");
        // Update doctor in store
        if (res.data.data) {
          setDoctor((prev) => ({ ...prev, ...res.data.data }));
        }
      } else {
        setEmailVerificationRequired(!checked); // Revert on error
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "Failed to update email verification setting"
      );
      setEmailVerificationRequired(!checked); // Revert on error
    }
  };

  const handleDisconnectGoogle = async () => {
    setLoading(true);
    try {
      const doctorId = doctor?._id;
      if (!doctorId) {
        toast.error("Doctor ID not found");
        return;
      }

      const res = await axios.put(
        `${BaseUrl}/doctor/doctorProfile/update/${doctorId}`,
        {
          disconnectGoogle: true,
        },
        { withCredentials: true }
      );

      if (res.data.success || res.status === 200) {
        toast.success("Google account disconnected successfully");
        setShowDisconnectGoogleModal(false);
        // Update doctor in store
        if (res.data.data) {
          setDoctor((prev) => ({ ...prev, ...res.data.data }));
        }
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to disconnect Google account"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAllBrowsers = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${BaseUrl}/doctor/logout-all`,
        {},
        { withCredentials: true }
      );
      if (res.data.success || res.status === 200) {
        toast.success("Logged out of all other browsers successfully");
        setShowLogoutAllModal(false);
      } else {
        toast.error(res.data.message || "Failed to logout from other browsers");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to logout from other browsers"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='max-w-3xl'>
      <h2 className='mb-6 border-b border-gray-200 pb-3 text-2xl font-semibold text-gray-900'>
        Account Settings
      </h2>

      {/* Email Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <label className='mb-2 block text-sm font-medium text-gray-700'>
          Email
        </label>
        <div className='flex items-center justify-between'>
          <div className='flex flex-col gap-2'>
            <div>
              <span className='text-gray-900'>{email}</span>
              <span className='ml-2 text-gray-600'>Primary Email</span>
            </div>
            {secondaryEmail && (
              <div>
                <span className='text-gray-900'>{secondaryEmail}</span>
                <span className='ml-2 text-gray-600'>Secondary Email</span>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowAddEmailModal(true)}
            className='text-sm text-blue-600 underline hover:text-blue-800'
          >
            Add Another Email Address
          </button>
        </div>
      </div>

      {/* Password Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <label className='mb-2 block text-sm font-medium text-gray-700'>
          Password
        </label>
        <button
          onClick={() => setShowChangePasswordModal(true)}
          className='text-sm text-blue-600 underline hover:text-blue-800'
        >
          Change Password
        </button>
      </div>

      {/* Country of residence Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <label className='mb-2 block text-sm font-medium text-gray-700'>
          Country of residence
        </label>
        <div className='flex items-center justify-between'>
          <span className='text-gray-900'>{country}</span>
          <button
            onClick={() => setShowCountryInfoModal(true)}
            className='text-sm text-gray-500 underline hover:text-gray-700'
          >
            Learn more
          </button>
        </div>
      </div>

      {/* Logout Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <label className='mb-2 block text-sm font-medium text-gray-700'>
          Logout
        </label>
        <button
          onClick={() => setShowLogoutAllModal(true)}
          className='text-sm text-blue-600 underline hover:text-blue-800'
          disabled={loading}
        >
          Log out of all other browsers
        </button>
      </div>

      {/* Login security Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <label className='mb-2 block text-sm font-medium text-gray-700'>
          Login security
        </label>
        <div className='flex items-center justify-between'>
          <span className='text-gray-900'>Require email verification</span>
          <label className='relative inline-flex cursor-pointer items-center'>
            <input
              type='checkbox'
              checked={emailVerificationRequired}
              onChange={(e) => handleEmailVerificationToggle(e.target.checked)}
              className='peer sr-only'
            />
            <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"></div>
          </label>
        </div>
      </div>

      {/* Connected Accounts & Contacts Section */}
      <div className='mb-8'>
        <div className='mb-6 flex items-center justify-between border-b border-gray-200 pb-3'>
          <h3 className='text-xl font-semibold text-gray-900'>
            Connected Accounts & Contacts
          </h3>
          <button className='text-sm text-gray-500 underline hover:text-gray-700'>
            Learn more
          </button>
        </div>

        {/* Facebook */}
        <div className='mb-6 border-b border-gray-200 pb-6'>
          <div className='flex items-center justify-between'>
            <label className='block text-sm font-medium text-gray-700'>
              Facebook
            </label>
            <button className='text-sm text-blue-600 underline hover:text-blue-800'>
              Connect Facebook Account
            </button>
          </div>
        </div>

        {/* Google */}
        <div>
          <div className='flex items-center justify-between'>
            <label className='block text-sm font-medium text-gray-700'>
              Google
            </label>
            {isGoogleConnected ? (
              <div className='flex items-center gap-3'>
                <span className='text-gray-900'>{googleEmail}</span>
                <button
                  onClick={() => setShowDisconnectGoogleModal(true)}
                  className='text-sm text-red-600 underline hover:text-red-800'
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button className='text-sm text-blue-600 underline hover:text-blue-800'>
                Connect Google Account
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Add Email Modal */}
      {showAddEmailModal && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddEmailModal(false);
              setNewEmail("");
            }
          }}
        >
          <div className='mx-4 w-full max-w-md rounded-lg bg-white p-6'>
            <h3 className='mb-4 text-lg font-semibold'>
              Add Another Email Address
            </h3>
            <div className='mb-4'>
              <label className='mb-2 block text-sm font-medium text-gray-700'>
                Email Address
              </label>
              <input
                type='email'
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder='Enter email address'
                className='w-full rounded border px-3 py-2 text-sm'
              />
            </div>
            <div className='flex justify-end gap-3'>
              <button
                onClick={() => {
                  setShowAddEmailModal(false);
                  setNewEmail("");
                }}
                className='rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50'
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleAddEmail}
                disabled={loading}
                className='rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50'
              >
                {loading ? "Adding..." : "Add Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowChangePasswordModal(false);
              setOldPassword("");
              setNewPassword("");
              setConfirmPassword("");
            }
          }}
        >
          <div className='mx-4 w-full max-w-md rounded-lg bg-white p-6'>
            <h3 className='mb-4 text-lg font-semibold'>Change Password</h3>
            <div className='space-y-4'>
              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700'>
                  Old Password
                </label>
                <input
                  type='password'
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder='Enter old password'
                  className='w-full rounded border px-3 py-2 text-sm'
                />
              </div>
              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700'>
                  New Password
                </label>
                <input
                  type='password'
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder='Enter new password'
                  className='w-full rounded border px-3 py-2 text-sm'
                />
              </div>
              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700'>
                  Confirm Password
                </label>
                <input
                  type='password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder='Confirm new password'
                  className='w-full rounded border px-3 py-2 text-sm'
                />
              </div>
            </div>
            <div className='mt-6 flex justify-end gap-3'>
              <button
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setOldPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                className='rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50'
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={loading}
                className='rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50'
              >
                {loading ? "Changing..." : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Country Info Modal */}
      {showCountryInfoModal && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCountryInfoModal(false);
            }
          }}
        >
          <div className='mx-4 w-full max-w-md rounded-lg bg-white p-6'>
            <h3 className='mb-4 text-lg font-semibold'>Country of Residence</h3>
            <p className='mb-4 text-sm text-gray-600'>
              Your country of residence is used to determine which services and
              features are available to you. This information helps us comply
              with local regulations and provide you with the most relevant
              experience.
            </p>
            <p className='mb-4 text-sm text-gray-600'>
              Your current country of residence is: <strong>{country}</strong>
            </p>
            <p className='mb-6 text-sm text-gray-600'>
              If you need to change your country of residence, please contact
              support.
            </p>
            <div className='flex justify-end'>
              <button
                onClick={() => setShowCountryInfoModal(false)}
                className='rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700'
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Google Modal */}
      {showDisconnectGoogleModal && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDisconnectGoogleModal(false);
            }
          }}
        >
          <div className='mx-4 w-full max-w-md rounded-lg bg-white p-6'>
            <h3 className='mb-4 text-lg font-semibold'>
              Disconnect Google Account
            </h3>
            <p className='mb-4 text-sm text-gray-600'>
              Are you sure you want to disconnect your Google account (
              {googleEmail})? You will need to use your email and password to
              sign in.
            </p>
            <div className='flex justify-end gap-3'>
              <button
                onClick={() => setShowDisconnectGoogleModal(false)}
                className='rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50'
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleDisconnectGoogle}
                disabled={loading}
                className='rounded bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50'
              >
                {loading ? "Disconnecting..." : "Disconnect"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout All Browsers Confirmation Modal */}
      {showLogoutAllModal && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowLogoutAllModal(false);
            }
          }}
        >
          <div className='mx-4 w-full max-w-md rounded-lg bg-white p-6'>
            <h3 className='mb-4 text-lg font-semibold'>
              Log out of all other browsers
            </h3>
            <p className='mb-4 text-sm text-gray-600'>
              This will log you out of all other browsers and devices where
              you're currently signed in. You will remain signed in on this
              browser.
            </p>
            <p className='mb-6 text-sm text-gray-600'>
              Are you sure you want to continue?
            </p>
            <div className='flex justify-end gap-3'>
              <button
                onClick={() => setShowLogoutAllModal(false)}
                className='rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50'
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleLogoutAllBrowsers}
                disabled={loading}
                className='rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50'
              >
                {loading ? "Logging out..." : "Log out of all other browsers"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
